import React, { useMemo } from "react";
import VariableCard from "./VariableCard";
import AddDataAttributeCard from "./AddDataAttributeCard";
import {
  EDITOR_KIND,
  getEditorKind,
  groupVariablesByKind,
} from "../../../common/CertificateOnboarding/utils/variables";
import { CARD_CLASS } from "../../../common/CertificateOnboarding/utils/styles";

const KIND_DISPLAY_ORDER = [
  EDITOR_KIND.SUBJECT,
  EDITOR_KIND.ASSET,
  EDITOR_KIND.QR,
];

function VariableConfigPanel({
  variableConfig,
  setVariableConfig,
  qrCodeType,
  onQrCodeTypeChange,
  userAddedVariableKeys = [],
  onAddVariable,
  onRemoveVariable,
  t,
  hideSvgPositioning = false,
  showAddCard = true,
}) {
  const orderedItems = useMemo(() => {
    const groups = groupVariablesByKind(variableConfig);
    return KIND_DISPLAY_ORDER.flatMap((kind) =>
      (groups[kind] || []).map(({ key, attrs }) => ({ key, attrs })),
    );
  }, [variableConfig]);

  const variableCount = orderedItems.length;

  return (
    <div
      id="cert-onboarding-variable-panel"
      className="space-y-4 scroll-smooth"
    >
      <div className="rounded-xl border border-cfCardBorder bg-cfSecondary px-4 py-4 sm:px-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-cfHeadingText">
              {t("certificateOnboarding:variables_panel_title")}
            </h2>
            <p className="text-sm text-cfGlobalText mt-1 leading-relaxed">
              {t("certificateOnboarding:variables_panel_subtitle")}
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-cfPrimaryMuted text-cfPrimary border border-cfPrimaryBorder">
            {t("certificateOnboarding:section_count", {
              count: variableCount,
            })}
          </span>
        </div>
      </div>

      {showAddCard && (
        <AddDataAttributeCard
          variableConfig={variableConfig}
          onAdd={onAddVariable}
          t={t}
        />
      )}

      {variableCount === 0 ? (
        <div className={`${CARD_CLASS} p-8 text-center`}>
          <p className="text-sm text-cfGlobalText">
            {t("certificateOnboarding:no_variables_add_hint")}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orderedItems.map(({ key, attrs }, index) => (
            <VariableCard
              key={key}
              variableKey={key}
              attrs={variableConfig[key] || attrs}
              editorKind={getEditorKind(key, attrs)}
              setVariableConfig={setVariableConfig}
              qrCodeType={qrCodeType}
              onQrCodeTypeChange={onQrCodeTypeChange}
              isUserAdded={userAddedVariableKeys.includes(key)}
              onRemove={
                userAddedVariableKeys.includes(key)
                  ? () => onRemoveVariable(key)
                  : undefined
              }
              t={t}
              defaultOpen={index === 0}
              hideSvgPositioning={hideSvgPositioning}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default VariableConfigPanel;
